/**
 * Shared mount strategy for ADIPA web-component widgets.
 *
 * Legacy ADIPA sites (adipa-cl / -mx / -co) ship large global stylesheets
 * (main.css) that would otherwise win the cascade against the widget's Tailwind
 * utility classes and visually break it (overlapping layout, lost spacing, etc.).
 * To make every widget self-contained — styled *only* by widgets.css and immune
 * to the host page's CSS — we mount it inside a Shadow Root and clone the host's
 * widgets.css `<link>` into that shadow tree. The browser serves the clone from
 * cache, so there is no extra network request.
 *
 * This isolation only activates when widgets.css is present in the host page as a
 * `<link>` element (i.e. legacy embedding via assets.blade.php). In Next.js /
 * adipa-front the CSS is bundled by the framework and there are no global-CSS
 * wars, so the widget mounts in Light DOM and behaves like a normal React tree.
 *
 * Note: overlays that portal to `document.body` (selects, dropdowns, sonner
 * toasts) still rely on the *global* widgets.css `<link>` in the host head, which
 * legacy embeds always load — so they remain styled even though they render
 * outside the shadow root.
 */

const WIDGETS_CSS_SELECTOR = 'link[href*="widgets.css"]';

function findWidgetsCssLink(): HTMLLinkElement | null {
  return document.querySelector<HTMLLinkElement>(WIDGETS_CSS_SELECTOR);
}

function initShadow(host: HTMLElement, cssHref: string): ShadowRoot {
  const shadow = host.attachShadow({ mode: 'open' });

  // Clone the widgets.css <link> — browser serves it from cache (zero network).
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = cssHref;
  shadow.appendChild(link);

  // Some libraries (e.g. sonner) inject their CSS into document.head at module
  // load time. Clone those <style> tags so any of that UI rendered inside the
  // shadow root keeps its styling.
  const sonnerStyle = Array.from(
    document.querySelectorAll<HTMLStyleElement>('head > style'),
  ).find((style) => style.textContent?.includes('[data-sonner-toaster]'));
  if (sonnerStyle) {
    shadow.appendChild(sonnerStyle.cloneNode(true));
  }

  return shadow;
}

export interface WidgetMountOptions {
  /**
   * Optional data-* attribute name (set with an empty value) applied to the
   * mount node, useful as a host-side hook (e.g. `data-adipa-footer-widget-root`).
   */
  rootAttribute?: string;
}

/**
 * Creates the React mount node for a widget and attaches it either inside a
 * Shadow Root (legacy hosts that loaded widgets.css as a `<link>`) or in Light
 * DOM (bundled hosts). Returns the node to hand to React's `createRoot`.
 *
 * Idempotent with respect to the shadow root: if the host already has one it is
 * reused rather than re-created.
 */
export function createWidgetMount(
  host: HTMLElement,
  options: WidgetMountOptions = {},
): HTMLElement {
  const mountNode = document.createElement('div');
  if (options.rootAttribute) {
    mountNode.setAttribute(options.rootAttribute, '');
  }

  const widgetsLink = findWidgetsCssLink();
  if (widgetsLink) {
    const shadow = host.shadowRoot ?? initShadow(host, widgetsLink.href);
    shadow.appendChild(mountNode);
  } else {
    host.appendChild(mountNode);
  }

  return mountNode;
}
